import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { apiService } from '../../config/api';

interface Researcher {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  institution: string;
  department: string;
  position: string;
  researchArea: string;
  status: 'noprofile' | 'pending' | 'approved';
  approvedAt: string;
  lastLoginAt?: string;
  studyCount?: number;
}

const ResearcherManagement: React.FC = () => {
  const [researchers, setResearchers] = useState<Researcher[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedResearcher, setSelectedResearcher] = useState<Researcher | null>(null);
  const [applicationDialogOpen, setApplicationDialogOpen] = useState(false);
  const [applicationData, setApplicationData] = useState<any>(null);

  useEffect(() => {
    loadResearchers();
  }, []);

  const loadResearchers = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/api/admin/researchers?status=all');
      
      
      if (response.success) {
        setResearchers(response.researchers || []);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load researchers');
    } finally {
      setLoading(false);
    }
  };

  const handleViewApplication = async (researcher: Researcher) => {
    try {
      setSelectedResearcher(researcher);
      // Fetch the application data for this researcher
      const response = await apiService.get(`/api/admin/researcher-applications?email=${researcher.email}`);
      if (response.success && response.applications?.length > 0) {
        setApplicationData(response.applications[0]);
        setApplicationDialogOpen(true);
      }
    } catch (err: any) {
      // Error loading application data - silently fail
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'noprofile': return 'default';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (researchers.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" gutterBottom>
          No Researchers Found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No approved researchers found.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Researcher</TableCell>
              <TableCell>Institution</TableCell>
              <TableCell>Research Area</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Approved</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {researchers.map((researcher) => (
              <TableRow key={researcher.id} hover>
                <TableCell>
                  <Box>
                    <Typography variant="subtitle2">
                      {researcher.firstName} {researcher.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {researcher.email}
                    </Typography>
                  </Box>
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2">
                    {researcher.institution}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {researcher.department}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2">
                    {researcher.researchArea}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Chip
                    label={researcher.status.toUpperCase()}
                    color={getStatusColor(researcher.status)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(researcher.approvedAt)}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2">
                    {researcher.lastLoginAt ? formatDate(researcher.lastLoginAt) : 'Never'}
                  </Typography>
                </TableCell>
                
                <TableCell align="center">
                  <Tooltip title="View Application">
                    <IconButton 
                      size="small"
                      onClick={() => handleViewApplication(researcher)}
                    >
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Application Details Dialog */}
      <Dialog 
        open={applicationDialogOpen} 
        onClose={() => setApplicationDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Researcher Application Details
        </DialogTitle>
        <DialogContent>
          {selectedResearcher && applicationData && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Personal Information
                  </Typography>
                  <Typography variant="body2">
                    <strong>Name:</strong> {applicationData.firstName} {applicationData.lastName}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Email:</strong> {applicationData.email}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Date of Birth:</strong> {formatDate(applicationData.dateOfBirth)}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Professional Information
                  </Typography>
                  <Typography variant="body2">
                    <strong>Institution:</strong> {applicationData.institution}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Department:</strong> {applicationData.department || 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Position:</strong> {applicationData.position || 'N/A'}
                  </Typography>
                </Grid>
                
                {applicationData.description && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Research Description
                    </Typography>
                    <Typography variant="body2">
                      {applicationData.description}
                    </Typography>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Application Status
                  </Typography>
                  <Chip
                    label={applicationData.status?.toUpperCase() || 'PENDING'}
                    color={getStatusColor(applicationData.status || 'pending')}
                    size="small"
                    variant="outlined"
                  />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Submitted:</strong> {formatDate(applicationData.submittedAt)}
                  </Typography>
                  {applicationData.reviewedAt && (
                    <Typography variant="body2">
                      <strong>Reviewed:</strong> {formatDate(applicationData.reviewedAt)}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplicationDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResearcherManagement;
